import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  LayoutDashboard, Columns3, Briefcase, MapPin, Calculator,
  Search, Plus, LogOut, Menu, X, UserCircle, FileText, CalendarDays
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_contractor-pipeline/artifacts/xmrl9jcs_image_1764138784069.png";

const NAV_ITEMS = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/pipeline", icon: Columns3, label: "Pipeline" },
  { path: "/estimates", icon: FileText, label: "Estimates" },
  { path: "/calendar", icon: CalendarDays, label: "Calendar" },
  { path: "/jobs", icon: Briefcase, label: "Jobs" },
  { path: "/map", icon: MapPin, label: "Map" },
  { path: "/calculator", icon: Calculator, label: "Calculator" },
  { path: "/search", icon: Search, label: "Search" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 bg-white border-r border-zinc-200 z-40">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-200">
          <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-lg object-cover" />
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-zinc-900 truncate leading-tight">Picture Perfect</h2>
            <p className="text-xs text-zinc-500 truncate">Fence N Construction</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200
                ${isActive(item.path)
                  ? "bg-orange-50 text-orange-700 border border-orange-200"
                  : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="px-3 pb-4 space-y-2">
          <Button
            onClick={() => navigate("/pipeline?new=true")}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="sidebar-add-lead-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Lead
          </Button>
          <div className="flex items-center gap-2 px-3 py-2 border-t border-zinc-100 pt-3">
            <UserCircle className="w-5 h-5 text-zinc-400" />
            <span className="text-sm text-zinc-600 truncate flex-1">{user?.name || user?.email}</span>
            <button onClick={logout} className="text-zinc-400 hover:text-red-500 transition-colors" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 inset-x-0 bg-white border-b border-zinc-200 z-40">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
              {sidebarOpen ? <X className="w-6 h-6 text-zinc-700" /> : <Menu className="w-6 h-6 text-zinc-700" />}
            </button>
            <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-lg" />
            <span className="text-sm font-bold text-zinc-900">Picture Perfect</span>
          </div>
          <Button
            size="sm"
            onClick={() => navigate("/pipeline?new=true")}
            className="bg-orange-600 hover:bg-orange-700 text-white h-8 px-3 text-xs"
            data-testid="mobile-add-lead-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> Lead
          </Button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl animate-slide-in">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-200">
              <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-lg" />
              <h2 className="text-sm font-bold text-zinc-900">Picture Perfect</h2>
            </div>
            <nav className="px-3 py-4 space-y-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors
                    ${isActive(item.path) ? "bg-orange-50 text-orange-700" : "text-zinc-600 hover:bg-zinc-50"}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="px-3 pt-2 border-t border-zinc-100">
              <button onClick={logout} className="flex items-center gap-3 px-3 py-3 text-sm text-red-500 font-medium w-full" data-testid="mobile-logout-btn">
                <LogOut className="w-5 h-5" /> Logout
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:pl-64 pt-14 lg:pt-0 pb-36 lg:pb-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="bottom-nav lg:hidden flex items-center justify-around py-2 px-1" data-testid="bottom-nav">
        {NAV_ITEMS.slice(0, 5).map((item) => (
          <Link
            key={item.path}
            to={item.path}
            data-testid={`bottom-nav-${item.label.toLowerCase()}`}
            className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-xs transition-colors
              ${isActive(item.path) ? "text-orange-600 font-semibold" : "text-zinc-400"}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
